import {
  Button,
  Checkbox,
  DialogTitle,
  Divider,
  Drawer,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import useDefinedContext from "@entrilia/shared/hooks/useDefinedContext";
import { logError } from "@entrilia/shared/logging";
import adminApi from "../../../../../api/adminApi";
import type { AutoApproveOptions, WorkflowTemplateActivity } from "../../../../../api/types/workflowTypes";
import WorkflowTemplateDetailsContext from "../WorkflowTemplateDetailsContext";

const updateActivityInWorkflowTemplate = withErrorHandling(adminApi.updateActivityInWorkflowTemplate);

interface Props {
  activity: WorkflowTemplateActivity;
  onClose: () => void;
}

const WorkflowActivityDetailsDrawer = ({ activity, onClose }: Props) => {
  const { workflowTemplate, onActivityUpdated } = useDefinedContext(WorkflowTemplateDetailsContext);
  const { sendNotification, sendNotificationError } = useNotificationContext();

  const [agentTaskPrompt, setAgentTaskPrompt] = useState(activity.agentTaskPromptUserInput ?? "");
  const [agentValidationPrompt, setAgentValidationPrompt] = useState(activity.agentValidationPromptUserInput ?? "");
  const [autoApproveOption, setAutoApproveOption] = useState<AutoApproveOptions>(activity.autoApprove);
  const [updateActiveWorkflows, setUpdateActiveWorkflows] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    const [updatedActivity, error] = await updateActivityInWorkflowTemplate(workflowTemplate.id, activity.id, {
      agentTaskPromptUserInput: agentTaskPrompt.trim(),
      agentValidationPromptUserInput: agentValidationPrompt.trim(),
      autoApprove: autoApproveOption,
      updateActiveWorkflows,
    });
    setSaving(false);

    if (error) {
      logError(error, "[WorkflowActivityDetailsDrawer] updateActivityInWorkflowTemplate");
      sendNotificationError(error.message);
      return;
    }

    onActivityUpdated(updatedActivity);
    sendNotification("Activity updated successfully");
  };

  const handleReset = () => {
    setAgentTaskPrompt(activity.agentTaskPromptUserInput ?? "");
    setAgentValidationPrompt(activity.agentValidationPromptUserInput ?? "");
    setAutoApproveOption(activity.autoApprove);
    setUpdateActiveWorkflows(false);
  };

  const isDirty =
    agentTaskPrompt.trim() !== (activity.agentTaskPromptUserInput ?? "") ||
    agentValidationPrompt.trim() !== (activity.agentValidationPromptUserInput ?? "") ||
    autoApproveOption !== activity.autoApprove;

  return (
    <Drawer anchor="right" open onClose={onClose} sx={{ "& .MuiDrawer-paper": { width: "36rem" } }}>
      <DialogTitle>{activity.name}</DialogTitle>
      <DialogCloseButton onClick={onClose} />
      <Divider />
      <Stack useFlexGap spacing={3} sx={{ py: 2.5, px: 3, height: "100%", overflow: "scroll", flexGrow: 1 }}>
        <Stack spacing={1}>
          <Typography variant="subtitle2">Description</Typography>
          <Typography color="textSecondary">{activity.description}</Typography>
        </Stack>

        {activity.isAiPowered && (
          <>
            <Divider flexItem />

            <Stack spacing={2}>
              <Typography variant="subtitle2">Additional Agent Instructions</Typography>
              <TextField
                fullWidth
                multiline
                minRows={5}
                label="Agent Task Prompt"
                value={agentTaskPrompt}
                onChange={(e) => setAgentTaskPrompt(e.target.value)}
                disabled={!activity.agentTaskSystemPrompt}
              />
              <TextField
                fullWidth
                multiline
                minRows={5}
                label="Agent Validation Prompt"
                value={agentValidationPrompt}
                onChange={(e) => setAgentValidationPrompt(e.target.value)}
                disabled={!activity.agentValidationSystemPrompt}
              />
            </Stack>

            <Divider flexItem />

            <Stack>
              <FormControlLabel
                label="Enable auto-approve"
                control={
                  <Checkbox
                    checked={autoApproveOption === "AiAgentValidationPassed"}
                    onChange={(_, checked) => setAutoApproveOption(checked ? "AiAgentValidationPassed" : "Never")}
                  />
                }
              />
              <Typography variant="caption" color="textSecondary">
                Activity will be approved automatically in case AI Agent validation passes
              </Typography>
            </Stack>

            <Divider flexItem />

            <Stack spacing={1} useFlexGap>
              <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                <Button variant="contained" color="primary" onClick={handleSave} disabled={!isDirty} loading={saving}>
                  Save
                </Button>
                <Button variant="text" color="secondary" onClick={handleReset} disabled={!isDirty}>
                  Cancel
                </Button>
              </Stack>

              {isDirty && (
                <FormControlLabel
                  label="Also update active workflows with these settings"
                  control={
                    <Checkbox
                      checked={updateActiveWorkflows}
                      onChange={(_, checked) => setUpdateActiveWorkflows(checked)}
                    />
                  }
                />
              )}
            </Stack>
          </>
        )}
      </Stack>
    </Drawer>
  );
};

export default WorkflowActivityDetailsDrawer;
